import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";
import { uploadOnboardingPhoto } from "../api";
import ImageCropper from "../../../components/ImageCropper";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
} from "../../../helpers/imageUpload";

/**
 * The frame is one box at `aspectRatio` and at most `width` pixels wide whether
 * empty, uploading or filled, so choosing a photo never changes its size.
 *
 * `compact` shows the upload icon without its text. It is the caller's to set
 * rather than derived from `width`, because `width` is only a maximum: the
 * frame is `width: 100%` under it, so on a narrow screen it says nothing about
 * how wide the box actually renders.
 */
const PhotoDropzone = ({
  label,
  hint,
  hideLabel = false,
  compact = false,
  photo,
  onChange,
  onRemove,
  onUploadingChange,
  onReauthenticate,
  focusRef,
  validationError = "",
  required = false,
  aspectRatio = 3 / 4,
  width = 200,
}) => {
  const inputRef = useRef(null);
  const frameRef = useRef(null);
  // Moving between elements inside the frame fires a leave while the file is
  // still over it, so the highlight follows a count rather than the last event.
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSelection, setCropSelection] = useState(null);
  const cropSourceRef = useRef(null);

  const revokeCropSource = () => {
    if (cropSourceRef.current && typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(cropSourceRef.current);
    }
    cropSourceRef.current = null;
  };

  useEffect(
    () => () => {
      const source = cropSourceRef.current;
      if (source && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(source);
      }
      cropSourceRef.current = null;
    },
    [],
  );

  const closeCropper = () => {
    revokeCropSource();
    setCropSelection(null);
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    onUploadingChange?.(true);

    try {
      // Uploaded with the onboarding session specifically. The endpoint tags
      // the S3 object with the uploader's id and `/v2/onboarding/complete`
      // rejects a photo whose tag does not match the caller, so an upload made
      // without this member's token produces a 403 three screens later.
      const url = await uploadOnboardingPhoto(file);
      onChange({
        name: file.name,
        type: file.type,
        size: file.size,
        url,
      });
    } catch (error) {
      console.error("Profile photo upload failed:", error);
      if (error?.sessionExpired) {
        setSessionExpired(true);
        setError(t("onboarding.session.expired"));
        return;
      }
      setError(t("onboarding.profile.uploadFailed"));
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const selectPhoto = async (file) => {
    // The frame is disabled while uploading, but a drop still reaches here.
    if (!file || uploading) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      // Type before size: when both are wrong, the format is the more useful
      // thing to say first.
      setError(t("onboarding.profile.invalidPhoto"));
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError(t("onboarding.profile.photoTooLarge"));
      return;
    }

    setError("");
    revokeCropSource();
    const source = URL.createObjectURL(file);
    cropSourceRef.current = source;
    setCropSelection({ file, source });
  };

  const handleCrop = async (croppedFile) => {
    if (!cropSelection) return;

    // The cropper emits a JPEG named `croppedImage.jpg`. Keep the member's
    // filename for the profile payload while using the cropped bytes and type.
    const uploadFile = new File([croppedFile], cropSelection.file.name, {
      type: croppedFile.type || cropSelection.file.type,
      lastModified: cropSelection.file.lastModified,
    });

    if (uploadFile.size > MAX_IMAGE_BYTES) {
      setError(t("onboarding.profile.photoTooLarge"));
      return;
    }

    closeCropper();
    await uploadPhoto(uploadFile);
  };

  return (
    <Box>
      {/* The frame and its input carry `label` as their accessible name either
          way, so hiding this hides a duplicate, not the only naming. What it
          must not hide is the requirement, which rides on the frame instead. */}
      {!hideLabel && (
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {label}
          {required ? " *" : ""}
        </Typography>
      )}
      {hint && (
        <Typography
          sx={{
            mb: 1.5,
            fontSize: "0.8125rem",
            color: onboardingTokens.mutedForeground,
          }}
        >
          {hint}
        </Typography>
      )}
      <Box
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          selectPhoto(event.dataTransfer.files?.[0]);
        }}
        sx={{ position: "relative", width: "100%", maxWidth: width }}
      >
        <ButtonBase
          type="button"
          ref={(node) => {
            frameRef.current = node;
            if (focusRef) focusRef.current = node;
          }}
          onClick={() => inputRef.current?.click()}
          aria-label={photo ? t("onboarding.profile.replace") : label}
          aria-required={required || undefined}
          disabled={uploading}
          sx={{
            display: "flex",
            width: "100%",
            aspectRatio,
            overflow: "hidden",
            borderRadius: onboardingTokens.radius,
            border: `2px dashed ${dragging ? onboardingTokens.teal : onboardingTokens.track}`,
            backgroundColor: dragging
              ? onboardingTokens.lightTeal
              : onboardingTokens.card,
          }}
        >
          {uploading ? (
            compact ? (
              <CircularProgress
                size={24}
                aria-label={t("onboarding.profile.uploading")}
              />
            ) : (
              <Typography sx={{ color: onboardingTokens.mutedForeground }}>
                {t("onboarding.profile.uploading")}
              </Typography>
            )
          ) : photo ? (
            <Box
              component="img"
              src={photo.preview ?? photo.url}
              alt={photo.name}
              onError={(event) => {
                if (
                  photo.url &&
                  event.currentTarget.dataset.fallback !== "true"
                ) {
                  event.currentTarget.dataset.fallback = "true";
                  event.currentTarget.src = photo.url;
                }
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box sx={{ p: compact ? 1 : 2, textAlign: "center" }}>
              <AddPhotoAlternateOutlinedIcon
                sx={{
                  color: onboardingTokens.teal,
                  fontSize: compact ? 28 : 36,
                  mb: compact ? 0 : 1,
                }}
              />
              {!compact && (
                <Typography sx={{ color: onboardingTokens.mutedForeground }}>
                  {t("onboarding.profile.upload")}
                </Typography>
              )}
            </Box>
          )}
        </ButtonBase>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          aria-label={`${label} file`}
          hidden
          onChange={async (event) => {
            await selectPhoto(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        {cropSelection && (
          <ImageCropper
            open
            image={cropSelection.source}
            aspectRatio={aspectRatio}
            loading={uploading}
            error={error}
            onCrop={handleCrop}
            onCancel={closeCropper}
          />
        )}

        {photo && onRemove && (
          <ButtonBase
            type="button"
            aria-label={t("onboarding.profile.remove", { name: photo.name })}
            disabled={uploading}
            onClick={() => {
              onRemove();
              // This button unmounts with the photo; the frame stays.
              frameRef.current?.focus();
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: "50%",
              color: "secondary.contrastText",
              backgroundColor: "rgba(1, 47, 124, 0.86)",
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
      </Box>
      {(error || validationError) && !cropSelection && (
        <Typography
          role="alert"
          sx={{ color: onboardingTokens.destructive, mt: 1 }}
        >
          {error || validationError}
        </Typography>
      )}
      {sessionExpired && onReauthenticate && (
        <Button type="button" onClick={onReauthenticate}>
          {t("onboarding.session.signIn")}
        </Button>
      )}
    </Box>
  );
};

export default PhotoDropzone;
